/**
 * KOL 頁面模組
 */

//-----------------------------------------------------------------------
// 載入模組
import * as React from 'react';
import { JSX } from 'react';
import get from 'lodash/get';
import { useRecordContext, usePermissions, Pagination, ArrayField, SingleFieldList, ChipField, ArrayInput, SimpleFormIterator, Labeled, ReferenceArrayField, NumberField } from 'react-admin';
import { Create, Edit, List, Show, SimpleForm, TabbedShowLayout, Tab, Datagrid, ReferenceManyField } from 'react-admin';
import { TextInput, SelectInput, TextField, DateField, ShowButton, ReferenceField } from 'react-admin';
import { maxLength, required, downloadCSV } from 'react-admin';
import { SaveOnlyToolbar, ListEditButton, MemberLogTypeField, MemberLogDataField, TextUrlField, TimeRangeInput } from '../lib/components';
import jsonExport from 'jsonexport/dist';

//-----------------------------------------------------------------------
// defines
import { COMMON_THREE_STATUS, MEMBER_GROUP, MEMBER_LOG_TYPE } from '../common/defines';
import { NamePairInfo } from '../common/interfaces';
import { MemberAuthInfo } from '../providers/authProvider';
import { ChannelStatusField } from './channel';

//-----------------------------------------------------------------------
/** KOL 狀態顯示欄位元件。 */

const kolStatusMap = new Map<string, string>();
const kolStatusList : NamePairInfo<string>[] =
[
	{ id: COMMON_THREE_STATUS.Enable,	name: "啟用" },
	{ id: COMMON_THREE_STATUS.Disable,	name: "停用" },
	{ id: COMMON_THREE_STATUS.Deleted,	name: "已刪除" }
];

for(let status of kolStatusList)
	kolStatusMap.set(status.id, status.name);

export const KolStatusField = (props: any) =>
{
	const { source } = props;
	const record = useRecordContext(props);

	return (<span className="MuiTypography-body2 MuiTypography-root">{kolStatusMap.get(get(record, source))}</span>);
}

KolStatusField.defaultProps =
{
	label: '狀態',
	addLabel: true,
};

//-----------------------------------------------------------------------
/** KOL 列表過濾器。 */
const kolFilters =
[
	<TimeRangeInput key='input-time-range' alwaysOn />
];

/** 資料輸出功能。 */
const exporter = (records: any) =>
{
	const data = records.map((record: any) =>
	({
		...record
	}));

	return jsonExport(data,
	{
		headers	: [ 'id', 'name', 'description', 'tags.text', 'targetCount', 'targetIncrease', 'viewCount', 'viewIncrease', 'viewOfNew', 'commentCount', 'commentIncrease', 'commentOfNew', 'status', 'createTime' ],
		rename	: [ 'id', '名稱', '描述', '標籤', '目標數量', '目標增加量', '累計瀏覽量', '瀏覽增加量', '新增目標瀏覽量', '留言數量', '留言增加量', '新增目標留言量', '狀態', '建立時間' ],
	}, (err, csv) =>
	{
		downloadCSV("\ufeff" + csv, 'kol');
	});
};

/** KOL 列表頁面。 */
export const KolList = (props: any) =>
{
	let { permissions } = usePermissions();
	let	authInfo = permissions as MemberAuthInfo;

	return <List {...props} title='KOL' filters={kolFilters} exporter={exporter} >
		<Datagrid bulkActionButtons={false}>
			<TextField source='id' />
			<TextField source='name' label='名稱' />
			<TextField source='description' label='描述' />
			<ArrayField source="tags" label='標籤' sortable={false}>
				<SingleFieldList>
					<ChipField source="text" />
				</SingleFieldList>
			</ArrayField>
			<NumberField source='targetCount' label='目標數量' />
			<NumberField source='targetIncrease' label='目標增加量' />
			<NumberField source='viewCount' label='累計瀏覽量' />
			<NumberField source='viewIncrease' label='瀏覽增加量' />
			<NumberField source='viewOfNew' label='新增目標瀏覽量' />
			<NumberField source='reactCount' label='累計反應量' />
			<NumberField source='reactIncrease' label='反應增加量' />
			<NumberField source='reactOfNew' label='新增目標反應量' />
			<NumberField source='commentCount' label='留言數量' />
			<NumberField source='commentIncrease' label='留言增加量' />
			<NumberField source='commentOfNew' label='新增目標留言量' />
			<KolStatusField source='status' />
			<DateField source='createTime' label='建立時間' showTime />
			<ListEditButton edit={authInfo.kolEdit} />
			<ShowButton />
		</Datagrid>
	</List>;
}

/** KOL 資訊頁面。 */
export const KolShow = (props: any) =>
{
	let { permissions } = usePermissions();
	let	authInfo = permissions as MemberAuthInfo;
	let opLogs : JSX.Element = <></>;

	if(authInfo.group <= MEMBER_GROUP.Manager && authInfo.memberCreate)
	{
		opLogs = <Tab label='操作記錄'>
			<ReferenceManyField reference='memberLog' target='ml_int1' filter={{ ml_int1: props.id, 'type>=': MEMBER_LOG_TYPE.KolCreate, 'type<': MEMBER_LOG_TYPE.ChannelModify }} pagination={<Pagination />}>
				<Datagrid bulkActionButtons={false}>
					<TextField source='id' />
					<ReferenceField label="操作者" source="memberId" reference="member">
						<TextField source="name" />
					</ReferenceField>
					<MemberLogTypeField label='執行動作' />
					<MemberLogDataField label='動作內容' />
					<DateField source='logTime' label='紀錄時間' showTime />
					<TextField source='remoteAddress' label='遠端位址' />
				</Datagrid>
			</ReferenceManyField>
		</Tab>;
	}

	return <Show {...props}>
		<TabbedShowLayout>
			<Tab label='所屬頻道'>
				<ReferenceManyField reference='channel' target='c_o_id' filter={{ c_o_id: props.id }} pagination={<Pagination />} sort={{ field: 'viewCount', order: 'DESC' }} >
					<Datagrid bulkActionButtons={false}>
						<TextField source='id' />
						<ReferenceField label="資料來源" source="sourceId" reference="source" sortBy='s_id'>
						<TextField source="name" />
						</ReferenceField>
						<ReferenceField label="區域" source="regionId" reference="region">
							<TextField source="name" />
						</ReferenceField>
						<ReferenceField label="所屬 KOL" source="kolId" reference="kol">
							<TextField source="name" />
						</ReferenceField>
						<TextUrlField source='url' textSource='name' label='頻道連結' target='_blank' sortable={false} />
						<ReferenceArrayField label="關鍵字" source="keywordIds" reference="keyword" sortable={false} >
							<SingleFieldList>
								<ChipField source="text" />
							</SingleFieldList>
						</ReferenceArrayField>
						<NumberField source='targetCount' label='目標數量' />
						<NumberField source='viewCount' label='累計瀏覽量' />
						<NumberField source='reactCount' label='累計反應量' />
						<NumberField source='commentCount' label='留言數量' />
						<ChannelStatusField source='status' />
						<DateField source='createTime' label='資料加入時間' showTime />
					</Datagrid>
				</ReferenceManyField>
			</Tab>
			<Tab label='總覽'>
				<TextField source='id' />
				<TextField source='name' label='名稱' />
				<TextField source='description' label='描述' />
				<ArrayField source="tags" label='標籤'>
					<SingleFieldList>
						<ChipField source="text" />
					</SingleFieldList>
				</ArrayField>
				<KolStatusField source='status' />
				<DateField source='createTime' label='建立時間' showTime />
			</Tab>
			{opLogs}
		</TabbedShowLayout>
	</Show>;
}

/** KOL 編輯頁面。 */
export const KolEdit = (props: any) => (
	<Edit {...props}>
		<SimpleForm toolbar={<SaveOnlyToolbar/>}>
			<Labeled label="ID"><TextField source='id' /></Labeled>
			<TextInput source='name' label="名稱" />
			<TextInput source='description' label="描述" />
			<ArrayInput source="tags" label='標籤'>
				<SimpleFormIterator inline>
					<TextInput source='text' label='標籤文字' />
				</SimpleFormIterator>
			</ArrayInput>
			<SelectInput source='status' choices={kolStatusList} label="狀態" />
			<Labeled label="建立時間"><DateField source='createTime' showTime /></Labeled>
		</SimpleForm>
	</Edit>
);

/** KOL 建立頁面。 */
export const KolCreate = (props: any) => (
	<Create {...props}>
		<SimpleForm>
			<TextInput source='name' label="名稱" validate={[required(), maxLength(100)]} />
			<TextInput source='description' label="描述" validate={[maxLength(200)]} defaultValue={''} />
			<ArrayInput source="tags" label="標籤">
				<SimpleFormIterator inline>
					<TextInput source='text' label='標籤文字' />
				</SimpleFormIterator>
			</ArrayInput>
			<SelectInput source='status' choices={kolStatusList} label="狀態" />
		</SimpleForm>
	</Create>
);
